import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Sheet from 'react-modal-sheet';
import Button from './common/Button';
import useIsMobile from '../hooks/useIsMobile';
import useDialogA11y from '../hooks/useDialogA11y';
import useLoadingAnnouncement from '../hooks/useLoadingAnnouncement';
import RevokeIcon from '../assets/Revoke.svg';
import './RevokeConfirmDialog.css';

const formatConsentDate = (dateString) => {
	if (!dateString) return '';
	const date = new Date(dateString);
	const parts = date.toString().split(' ');
	return `${parts[2]} ${parts[1]} ${parts[3]}`;
};

const getPurposeText = (consent) =>
	consent?.consentPurposeText ||
	consent?.consentDetails?.Purpose?.text ||
	'';

const getGivenOn = (consent) =>
	formatConsentDate(
		consent?.consentIntentUpdateTimestamp ||
			consent?.consentDetails?.createTime
	);

const getConsentAccountLines = (consent) => {
	const lines = [];

	if (consent?.consentDetails?.Accounts?.length) {
		consent.consentDetails.Accounts.forEach((account, index) => {
			const maskedNumber =
				account?.maskedAccNumber || account?.linkRefNumber || '';
			const fipName = account?.fipName || account?.fipId || '';
			const label = maskedNumber
				? fipName
					? `${fipName} • ${maskedNumber}`
					: maskedNumber
				: fipName;

			if (label) {
				lines.push({
					key: `${consent?.consentIntentId}-${index}`,
					label,
				});
			}
		});
		return lines;
	}

	if (consent?.accountDetails?.maskedAccNumber) {
		const maskedNumber = consent.accountDetails.maskedAccNumber;
		const fipName = consent?.fipName || consent.accountDetails.fipId || '';
		const label = fipName ? `${fipName} • ${maskedNumber}` : maskedNumber;
		if (label) {
			lines.push({ key: consent?.consentIntentId, label });
		}
	}

	return lines;
};

const getEntityGroupedSummaries = (consents = []) => {
	const groups = {};

	consents.forEach((consent) => {
		const entityName =
			consent?.consentIntentEntityName ||
			consent?.consentDetails?.FIU?.name ||
			'Unknown';

		if (!groups[entityName]) {
			groups[entityName] = {
				key: entityName,
				groupLabel: entityName,
				groupType: 'entity',
				consents: [],
			};
		}

		groups[entityName].consents.push({
			key: consent?.consentIntentId,
			purposeText: getPurposeText(consent),
			givenOn: getGivenOn(consent),
			accountLines: getConsentAccountLines(consent),
		});
	});

	return Object.values(groups);
};

const getAccountGroupedSummaries = (consents = []) => {
	const groups = {};

	const addConsentToGroup = (groupKey, accountLabel, consent) => {
		if (!groups[groupKey]) {
			groups[groupKey] = {
				key: groupKey,
				groupLabel: accountLabel,
				groupType: 'account',
				consents: [],
			};
		}

		const alreadyAdded = groups[groupKey].consents.some(
			(entry) => entry.key === consent?.consentIntentId
		);

		if (alreadyAdded) return;

		groups[groupKey].consents.push({
			key: consent?.consentIntentId,
			entityName:
				consent?.consentIntentEntityName ||
				consent?.consentDetails?.FIU?.name ||
				consent?.fipName ||
				'Unknown',
			purposeText: getPurposeText(consent),
			givenOn: getGivenOn(consent),
		});
	};

	consents.forEach((consent) => {
		if (consent?.consentDetails?.Accounts?.length) {
			consent.consentDetails.Accounts.forEach((account) => {
				const maskedNumber =
					account?.maskedAccNumber || account?.linkRefNumber || '';
				const fipName = account?.fipName || account?.fipId || '';
				const groupKey = `${account?.fipId || fipName}-${maskedNumber}`;
				const accountLabel = maskedNumber
					? fipName
						? `${fipName} • ${maskedNumber}`
						: maskedNumber
					: fipName || 'Account';

				addConsentToGroup(groupKey, accountLabel, consent);
			});
			return;
		}

		if (consent?.accountDetails?.maskedAccNumber) {
			const maskedNumber = consent.accountDetails.maskedAccNumber;
			const fipName =
				consent?.fipName || consent.accountDetails.fipId || '';
			const groupKey = `${consent.accountDetails.fipId || fipName}-${maskedNumber}`;
			const accountLabel = fipName
				? `${fipName} • ${maskedNumber}`
				: maskedNumber;

			addConsentToGroup(groupKey, accountLabel, consent);
			return;
		}

		addConsentToGroup(
			consent?.consentIntentId,
			consent?.consentIntentEntityName || 'Account',
			consent
		);
	});

	return Object.values(groups);
};

const getGroupedSummaries = (consents, groupByEntity) =>
	groupByEntity
		? getEntityGroupedSummaries(consents)
		: getAccountGroupedSummaries(consents);

const getBriefSummary = (groups, count, groupByEntity) => {
	if (groupByEntity) {
		const appCount = groups.length;

		if (appCount === 1) {
			return `${count} consent${count > 1 ? 's' : ''} · ${groups[0]?.groupLabel}`;
		}

		if (appCount <= 3) {
			return `${count} consents · ${groups.map((group) => group.groupLabel).join(', ')}`;
		}

		return `${count} consents · ${appCount} apps`;
	}

	const accountCount = groups.length;
	const entityNames = [
		...new Set(
			groups.flatMap((group) =>
				group.consents.map((consent) => consent.entityName)
			)
		),
	];

	if (accountCount === 1 && count === 1) {
		return groups[0]?.groupLabel || entityNames[0] || `${count} consent`;
	}

	if (accountCount === 1) {
		return `${count} consents for ${groups[0]?.groupLabel}`;
	}

	if (entityNames.length === 1) {
		return `${count} consents · ${entityNames[0]}`;
	}

	return `${count} consents · ${accountCount} accounts`;
};

const ConsentDetailsList = ({ groups, isDesktop = false }) => (
	<ul
		role="list"
		className={`revoke-confirm-account-list${isDesktop ? ' revoke-confirm-account-list--desktop' : ''}`}
	>
		{groups.map((group) => (
			<li key={group.key} role="listitem" className="revoke-confirm-account-item">
				<span
					className={`revoke-confirm-account-header${
						group.groupType === 'entity' ? ' revoke-confirm-account-header--entity' : ''
					}`}
				>
					{group.groupLabel}
				</span>
				<ul role="list" className="revoke-confirm-consent-list">
					{group.consents.map((consent) => (
						<li key={consent.key} role="listitem" className="revoke-confirm-consent-item">
							{group.groupType === 'entity' ? (
								<>
									<div className="revoke-confirm-consent-row">
										{consent.purposeText && (
											<span className="revoke-confirm-consent-entity">
												{consent.purposeText}
											</span>
										)}
										{consent.givenOn && (
											<span className="revoke-confirm-consent-date">
												Consent given on {consent.givenOn}
											</span>
										)}
									</div>
									{consent.accountLines?.length > 0 && (
										<div className="revoke-confirm-consent-accounts">
											{consent.accountLines.map((account) => (
												<span
													key={account.key}
													className="revoke-confirm-account-line"
												>
													{account.label}
												</span>
											))}
										</div>
									)}
								</>
							) : (
								<>
									<div className="revoke-confirm-consent-row">
										<span className="revoke-confirm-consent-entity">
											{consent.entityName}
										</span>
										{consent.givenOn && (
											<span className="revoke-confirm-consent-date">
												Consent given on {consent.givenOn}
											</span>
										)}
									</div>
									{consent.purposeText && (
										<span className="revoke-confirm-consent-purpose">
											{consent.purposeText}
										</span>
									)}
								</>
							)}
						</li>
					))}
				</ul>
			</li>
		))}
	</ul>
);

const ConsentSummarySection = ({
	groups,
	count,
	primaryColor,
	showDetails,
	onToggleDetails,
	isDesktop = false,
	groupByEntity = false,
}) => {
	const briefSummary = getBriefSummary(groups, count, groupByEntity);
	const detailsId = 'revoke-confirm-details-panel';

	return (
		<div
			className={`revoke-confirm-summary${
				isDesktop ? ' revoke-confirm-summary--desktop' : ''
			}`}
		>
			<p className="revoke-confirm-entities">{briefSummary}</p>
			<button
				type="button"
				className="revoke-confirm-details-toggle"
				onClick={onToggleDetails}
				aria-expanded={showDetails}
				aria-controls={detailsId}
				style={{ color: primaryColor }}
			>
				{showDetails ? 'Hide consent details' : 'View consent details'}
			</button>
			{showDetails && (
				<div
					id={detailsId}
					role="region"
					aria-label="Consent details"
					className={`revoke-confirm-details-panel${
						isDesktop ? ' revoke-confirm-details-panel--desktop' : ''
					}`}
				>
					<ConsentDetailsList groups={groups} isDesktop={isDesktop} />
				</div>
			)}
		</div>
	);
};

const APP_FOOTER_HEIGHT = 48;
const MAX_SHEET_HEIGHT_RATIO = 0.75;

const useSheetLayout = (isOpen, isMobile) => {
	const sheetContentRef = useRef(null);
	const [height, setHeight] = useState(280);
	const measureRafRef = useRef(0);

	useLayoutEffect(() => {
		if (!isOpen) {
			setHeight(280);
		}
	}, [isOpen]);

	useLayoutEffect(() => {
		if (!isOpen || !isMobile) return undefined;

		const measure = () => {
			cancelAnimationFrame(measureRafRef.current);
			measureRafRef.current = requestAnimationFrame(() => {
				const node = sheetContentRef.current;
				if (!node) return;

				const maxHeight = Math.floor(
					window.innerHeight * MAX_SHEET_HEIGHT_RATIO
				);
				const naturalHeight = Math.ceil(node.scrollHeight);
				if (!naturalHeight) return;

				const nextHeight = Math.min(naturalHeight, maxHeight);
				setHeight((prev) => (prev === nextHeight ? prev : nextHeight));
			});
		};

		measure();

		const node = sheetContentRef.current;
		const resizeObserver =
			node && typeof ResizeObserver !== 'undefined'
				? new ResizeObserver(measure)
				: null;
		if (resizeObserver && node) {
			resizeObserver.observe(node);
		}

		window.addEventListener('resize', measure);

		return () => {
			cancelAnimationFrame(measureRafRef.current);
			window.removeEventListener('resize', measure);
			resizeObserver?.disconnect();
		};
	}, [isOpen, isMobile]);

	return { sheetContentRef, height };
};

const RevokeConfirmDialog = ({
	isOpen,
	onClose,
	onConfirm,
	accounts = [],
	customDetails,
	isLoading = false,
	groupByEntity = false,
}) => {
	const isMobile = useIsMobile();
	const dialogRef = useDialogA11y(isOpen, onClose);
	const [showDetails, setShowDetails] = useState(false);
	const count = accounts.length;
	const summaryGroups = useMemo(
		() => getGroupedSummaries(accounts, groupByEntity),
		[accounts, groupByEntity]
	);
	const { sheetContentRef, height: sheetHeight } = useSheetLayout(
		isOpen,
		isMobile
	);

	useLoadingAnnouncement(isLoading, 'Revoking consents, please wait', '');

	const setSheetDialogRef = (node) => {
		sheetContentRef.current = node;
		dialogRef.current = node;
	};

	useEffect(() => {
		if (!isOpen) {
			setShowDetails(false);
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !isMobile) return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.classList.add('revoke-confirm-sheet-open');
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.classList.remove('revoke-confirm-sheet-open');
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, isMobile]);

	const title = count === 1 ? 'Revoke consent?' : `Revoke ${count} consents?`;
	const message =
		count === 1
			? 'Are you sure you want to revoke this consent? Data sharing will stop for this consent.'
			: `Are you sure you want to revoke ${count} selected consents? Data sharing will stop for these consents.`;

	const consentSummary = (
		<ConsentSummarySection
			groups={summaryGroups}
			count={count}
			primaryColor={customDetails?.primaryBtnColor}
			showDetails={showDetails}
			onToggleDetails={() => setShowDetails((prev) => !prev)}
			isDesktop={!isMobile}
			groupByEntity={groupByEntity}
		/>
	);

	const confirmButtonStyle = useMemo(
		() => ({
			height: customDetails?.btnHeight,
			width: isMobile ? '100%' : 'auto',
			borderRadius: customDetails?.borderRadius,
			fontFamily: customDetails?.fontFamily,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			gap: '8px',
			whiteSpace: 'nowrap',
			fontSize: '13px',
		}),
		[
			customDetails?.btnHeight,
			customDetails?.borderRadius,
			customDetails?.fontFamily,
			isMobile,
		]
	);

	const mobileActionButtons = useMemo(
		() => (
			<div
				className="revoke-confirm-sheet-actions"
				role="group"
				aria-label="Revoke confirmation actions"
			>
				<div className="revoke-confirm-sheet-actions-inner">
					<Button
						type="button"
						onClick={onConfirm}
						disabled={isLoading}
						aria-busy={isLoading}
						aria-label={
							isLoading ? 'Revoking consents' : 'Confirm revoke consent'
						}
						color={customDetails?.primaryBtnColor}
						style={confirmButtonStyle}
					>
						{isLoading ? (
							<div
								className="snippet"
								style={{ display: 'flex', justifyContent: 'center' }}
								aria-hidden="true"
							>
								<div className="stage">
									<div className="dot-pulse-btn"></div>
								</div>
							</div>
						) : (
							<>
								<img
									src={RevokeIcon}
									alt=""
									aria-hidden="true"
									style={{ width: '20px', height: '20px' }}
								/>
								Confirm revoke
							</>
						)}
					</Button>
					<Button
						type="button"
						onClick={onClose}
						disabled={isLoading}
						text="Cancel"
						color={customDetails?.primaryBtnColor}
						variant="outlined"
						borderColor={customDetails?.btnBorderColor}
						style={confirmButtonStyle}
					/>
				</div>
			</div>
		),
		[
			isLoading,
			onConfirm,
			onClose,
			customDetails?.primaryBtnColor,
			customDetails?.btnBorderColor,
			confirmButtonStyle,
		]
	);

	const desktopActionButtons = (
		<div className="revoke-confirm-actions revoke-confirm-actions--desktop">
			<Button
				type="button"
				onClick={onClose}
				disabled={isLoading}
				text="Cancel"
				color={customDetails?.primaryBtnColor}
				variant="outlined"
				borderColor={customDetails?.btnBorderColor}
				style={{
					height: customDetails?.btnHeight,
					minWidth: '140px',
					borderRadius: customDetails?.borderRadius,
					fontFamily: customDetails?.fontFamily,
					fontSize: '13px',
				}}
			/>
			<Button
				type="button"
				onClick={onConfirm}
				disabled={isLoading}
				aria-busy={isLoading}
				aria-label={isLoading ? 'Revoking consents' : 'Confirm revoke consent'}
				color={customDetails?.primaryBtnColor}
				style={{
					height: customDetails?.btnHeight,
					minWidth: '160px',
					borderRadius: customDetails?.borderRadius,
					fontFamily: customDetails?.fontFamily,
					fontSize: '13px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '8px',
				}}
			>
				{isLoading ? (
					<div
						className="snippet"
						style={{ display: 'flex', justifyContent: 'center' }}
						aria-hidden="true"
					>
						<div className="stage">
							<div className="dot-pulse-btn"></div>
						</div>
					</div>
				) : (
					<>
						<img
							src={RevokeIcon}
							alt=""
							aria-hidden="true"
							style={{ width: '18px', height: '18px' }}
						/>
						Confirm revoke
					</>
				)}
			</Button>
		</div>
	);

	if (!isOpen) return null;

	if (isMobile) {
		return (
			<Sheet
				isOpen={isOpen}
				onClose={onClose}
				snapPoints={[sheetHeight]}
				disableDrag={true}
				className="revoke-confirm-sheet"
			>
				<Sheet.Container style={{ bottom: APP_FOOTER_HEIGHT }}>
					<div
						ref={setSheetDialogRef}
						className="revoke-confirm-sheet-content"
						role="dialog"
						aria-modal="true"
						aria-labelledby="revoke-confirm-title"
					>
						<div className="revoke-confirm-sheet-body">
							<div className="revoke-confirm-sheet-message">
								<h2 id="revoke-confirm-title" className="revoke-confirm-title">
									{title}
								</h2>
								<p className="revoke-confirm-message">{message}</p>
								{consentSummary}
							</div>
						</div>
						{mobileActionButtons}
					</div>
				</Sheet.Container>
				<Sheet.Backdrop
					onTap={isLoading ? undefined : onClose}
					style={{
						top: 0,
						bottom: APP_FOOTER_HEIGHT,
						height: 'auto',
					}}
				/>
			</Sheet>
		);
	}

	return (
		<div
			className="revoke-confirm-backdrop"
			onClick={isLoading ? undefined : onClose}
		>
			<div
				ref={dialogRef}
				className="revoke-confirm-content revoke-confirm-content--desktop"
				role="dialog"
				aria-modal="true"
				aria-labelledby="revoke-confirm-title"
				onClick={(event) => event.stopPropagation()}
			>
				<button
					type="button"
					className="revoke-confirm-close"
					onClick={onClose}
					disabled={isLoading}
					aria-label="Close revoke confirmation dialog"
				>
					×
				</button>
				<div className="revoke-confirm-desktop-body">
					<h2 id="revoke-confirm-title" className="revoke-confirm-title">
						{title}
					</h2>
					<p className="revoke-confirm-message">{message}</p>
					{consentSummary}
				</div>
				{desktopActionButtons}
			</div>
		</div>
	);
};

export default RevokeConfirmDialog;
